import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Save, Loader2 } from 'lucide-react';
import { themeApi } from '../../api/theme';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import './ThemePanel.css';

/**
 * ThemePanel - Editable theme field with Generate/Save buttons
 *
 * Props:
 * - storyId: UUID of the story
 * - sessionId: UUID of the workshop session (for context in generation)
 * - onThemeSaved: (theme) => void - callback when theme is saved
 */
export default function ThemePanel({
  storyId,
  sessionId,
  onThemeSaved
}) {
  const [theme, setTheme] = useState('');
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [savedTheme, setSavedTheme] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Load existing theme on mount
  useEffect(() => {
    if (storyId) {
      loadTheme();
    }
  }, [storyId]);

  const loadTheme = async () => {
    try {
      setLoading(true);
      const response = await themeApi.getTheme(storyId);
      const data = response?.data || response;
      const existingTheme = data?.theme || '';
      setTheme(existingTheme);
      setSavedTheme(existingTheme);
    } catch (err) {
      console.error('Failed to load theme:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = useCallback(async () => {
    // If there's existing content, show confirmation
    if (theme.trim() && !showConfirm) {
      setShowConfirm(true);
      return;
    }

    const ok = await askPaid('theme', {
      hint: theme.trim()
        ? 'Replaces the current theme. Cancel spends nothing.'
        : undefined,
    });
    if (!ok) return;
    setShowConfirm(false);
    setGenerating(true);

    try {
      const response = await themeApi.generateTheme(storyId, sessionId);
      const data = response?.data || response;

      if (data.generated_theme) {
        setTheme(data.generated_theme);

        if (data.warning) {
          setToast({ type: 'warning', message: data.warning });
        } else {
          setToast({ type: 'success', message: 'Theme generated!' });
        }
      }
    } catch (err) {
      console.error('Failed to generate theme:', err);
      setToast({ type: 'error', message: 'Failed to generate theme' });
    } finally {
      setGenerating(false);
    }
  }, [storyId, sessionId, theme, showConfirm, askPaid]);

  const handleSave = async () => {
    if (!theme.trim()) {
      setToast({ type: 'error', message: 'Theme cannot be empty' });
      return;
    }

    setSaving(true);

    try {
      const response = await themeApi.saveTheme(storyId, theme.trim());
      const data = response?.data || response;

      setSavedTheme(data.theme);
      setToast({ type: 'success', message: 'Theme saved!' });
      onThemeSaved?.(data.theme);
    } catch (err) {
      console.error('Failed to save theme:', err);
      setToast({ type: 'error', message: 'Failed to save theme' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const isDirty = theme !== savedTheme;

  // Clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  if (loading) {
    return (
      <div className="theme-panel theme-panel--loading">
        <Loader2 className="theme-panel__spinner" />
        <span>Loading theme...</span>
      </div>
    );
  }

  return (
    <div className="theme-panel">
      {paidDialog}
      <div className="theme-panel__header">
        <h3 className="theme-panel__title">Current Theme</h3>
        {isDirty && <span className="theme-panel__dirty">Unsaved changes</span>}
      </div>

      <div className="theme-panel__content">
        <textarea
          className="theme-panel__input"
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          placeholder="What is your story philosophically about? Enter a theme statement (5-15 words)..."
          rows={2}
          maxLength={200}
        />

        <div className="theme-panel__hint">
          Aim for a single sentence under 15 words. Examples: "The cost of ambition" or "Can love transcend boundaries?"
        </div>
      </div>

      <div className="theme-panel__actions">
        <button
          className="theme-panel__btn theme-panel__btn--generate"
          onClick={handleGenerate}
          disabled={generating || saving}
        >
          {generating ? (
            <>
              <Loader2 className="theme-panel__btn-icon theme-panel__btn-icon--spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="theme-panel__btn-icon" />
              Generate Theme
            </>
          )}
        </button>

        <button
          className="theme-panel__btn theme-panel__btn--save"
          onClick={handleSave}
          disabled={saving || generating || !isDirty}
        >
          {saving ? (
            <>
              <Loader2 className="theme-panel__btn-icon theme-panel__btn-icon--spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="theme-panel__btn-icon" />
              Save Theme
            </>
          )}
        </button>
      </div>

      {/* Confirmation dialog for Generate overwrite */}
      {showConfirm && (
        <div className="theme-panel__confirm">
          <p>Replace current theme with AI suggestion?</p>
          <div className="theme-panel__confirm-actions">
            <button onClick={handleCancelConfirm} className="theme-panel__confirm-btn">
              Cancel
            </button>
            <button onClick={handleGenerate} className="theme-panel__confirm-btn theme-panel__confirm-btn--primary">
              Replace
            </button>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`theme-panel__toast theme-panel__toast--${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
